import React, { useState, useEffect, useRef } from 'react';
import { languages } from '../../services/api';
import './AddLanguageModal.css';

const PROFICIENCY_LEVELS = [
  { value: 'beginner', label: 'Beginner', description: 'Just starting out' },
  { value: 'intermediate', label: 'Intermediate', description: 'Basic conversations' },
  { value: 'advanced', label: 'Advanced', description: 'Fluent conversations' },
  { value: 'fluent', label: 'Fluent', description: 'Native-like proficiency' },
];

// Searchable dropdown component
const SearchableLanguageSelect = ({ value, onChange, supportedLanguages, placeholder, label, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Get selected language object
  const selectedLanguage = supportedLanguages.find(lang => lang.code === value);

  // Filter languages based on search term
  const filteredLanguages = supportedLanguages.filter(lang =>
    lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lang.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (langCode) => {
    onChange(langCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-select" ref={dropdownRef}>
      <label htmlFor={id}>{label}</label>
      <div className="select-container">
        <div
          className="select-display"
          onClick={() => setIsOpen(!isOpen)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsOpen(!isOpen);
            }
          }}
        >
          {selectedLanguage ? (
            <span>
              {selectedLanguage.flagEmoji} {selectedLanguage.name} ({selectedLanguage.nativeName})
            </span>
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
          <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
        </div>

        {isOpen && (
          <div className="select-dropdown">
            <input
              type="text"
              className="select-search"
              placeholder="Search languages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            <div className="select-options">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map((lang) => (
                  <div
                    key={lang.code}
                    className={`select-option ${value === lang.code ? 'selected' : ''}`}
                    onClick={() => handleSelect(lang.code)}
                  >
                    {lang.flagEmoji} {lang.name} ({lang.nativeName})
                  </div>
                ))
              ) : (
                <div className="select-option-empty">No languages found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddLanguageModal = ({ onClose, onLanguageAdded }) => {
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    languageCode: '',
    nativeLanguageCode: '',
    proficiencyLevel: 'beginner',
  });

  useEffect(() => {
    fetchSupportedLanguages();
  }, []);

  const fetchSupportedLanguages = async () => {
    try {
      setLoading(true);
      const response = await languages.getSupported();
      setSupportedLanguages(response.data.languages || []);
    } catch (error) {
      console.error('Failed to fetch supported languages:', error);
      setError('Failed to load supported languages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.languageCode) {
      setError('Please select a language to learn');
      return;
    }

    if (!formData.nativeLanguageCode) {
      setError('Please select your native language');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await languages.create(formData);
      onLanguageAdded();
    } catch (error) {
      console.error('Failed to create language:', error);
      if (error.response?.status === 409) {
        setError('You are already learning this language');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to add language. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-language-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add a New Language</h3>
          <button className="btn-close-x" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Loading languages...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <SearchableLanguageSelect
                id="target-language"
                label="Language you want to learn"
                placeholder="-- Search and select a language --"
                value={formData.languageCode}
                onChange={(code) => setFormData({ ...formData, languageCode: code })}
                supportedLanguages={supportedLanguages}
              />
            </div>

            <div className="form-group">
              <SearchableLanguageSelect
                id="native-language"
                label="Your native language"
                placeholder="-- Search and select your native language --"
                value={formData.nativeLanguageCode}
                onChange={(code) => setFormData({ ...formData, nativeLanguageCode: code })}
                supportedLanguages={supportedLanguages}
              />
            </div>

            <div className="form-group">
              <label>Proficiency Level</label>
              <div className="proficiency-options">
                {PROFICIENCY_LEVELS.map((level) => (
                  <label key={level.value} className="proficiency-option">
                    <input
                      type="radio"
                      name="proficiencyLevel"
                      value={level.value}
                      checked={formData.proficiencyLevel === level.value}
                      onChange={(e) => setFormData({ ...formData, proficiencyLevel: e.target.value })}
                    />
                    <div className="proficiency-label">
                      <strong>{level.label}</strong>
                      <span>{level.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || !formData.languageCode || !formData.nativeLanguageCode}
              >
                {submitting ? 'Adding...' : 'Add Language'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddLanguageModal;
